(ns yardwerkz.capra-dispatch
  "Capra-backed dispatch layer for mutations, queries, and subscriptions.

   Session capability is the single cap received at connect time.
   Auth identity is cached after first auth/login and passed as :auth in data."
  (:require
   [capra.core :as capra]
   [capra.callback :as callback]
   [capra.lifecycle]
   [capra.stub]
   [yardwerkz.capra-client :as capra-client]
   [yardwerkz.api :as api]
   [lambdaisland.glogi :as log]))

;; =============================================================================
;; Auth Cache
;; =============================================================================

;; Cached identity map (returned by :auth/login)
(defonce ^:private cached-identity (atom nil))

;; In-flight auth promise
(defonce ^:private auth-promise (atom nil))

;; Active role
(defonce ^:private active-role (atom nil))

(defn set-active-role!
  "Set the active role for Capra auth. Call during init and on role switch."
  [role]
  (when (not= role @active-role)
    (reset! active-role role)
    (reset! cached-identity nil)
    (reset! auth-promise nil)
    (log/info :capra-dispatch/role-changed {:role role})))

(defn reset-session!
  "Clear cached identity. Call on logout or token expiry."
  []
  (reset! cached-identity nil)
  (reset! auth-promise nil)
  (log/info :capra-dispatch/session-reset {}))

;; =============================================================================
;; Authentication
;; =============================================================================

(defn authenticate!
  "Obtain identity via :auth/login. Returns Promise<identity-map>.
   Deduplicates concurrent auth calls.
   Public so api-helpers can use it for the new ctx/await pattern."
  [get-token-fn]
  (if-let [cached @cached-identity]
    (js/Promise.resolve cached)
    (if-let [inflight @auth-promise]
      inflight
      (if-not get-token-fn
        (js/Promise.reject
         (ex-info "capra-dispatch: token provider not set" {}))
        (let [p (-> @capra-client/ready
                    (.then (fn [_] (get-token-fn)))
                    (.then (fn [token]
                             (let [ctx (capra/ctx @capra-client/client)
                                   ref (api/auth-login ctx {:token token
                                                            :role  @active-role})]
                               (capra/await ctx ref))))
                    (.then (fn [identity]
                             (reset! cached-identity identity)
                             (reset! auth-promise nil)
                             (log/info :capra-dispatch/authenticated {})
                             identity))
                    (.catch (fn [err]
                              (reset! auth-promise nil)
                              (log/error :capra-dispatch/auth-failed
                                         {:error (ex-message err)})
                              (throw err))))]
          (reset! auth-promise p)
          p)))))

;; =============================================================================
;; Subscriptions
;; =============================================================================

;; Static lookup table — gen-stubs produces these functions in yardwerkz.api
(def ^:private sub-fns
  {:entity/job         api/sub-job
   :entity/quote       api/sub-quote
   :entity/property    api/sub-property
   :entity/yard        api/sub-yard
   :entity/message     api/sub-message
   :topic/customer-jobs api/sub-customer-jobs
   :topic/worker-jobs  api/sub-worker-jobs
   :topic/job-quotes   api/sub-job-quotes
   :topic/job-messages api/sub-job-messages
   :topic/notifications api/sub-notifications
   :topic/unread-count api/sub-unread-count
   :topic/job-tracking api/sub-job-tracking
   :topic/customer-properties api/sub-customer-properties
   :topic/property-yards api/sub-property-yards
   :topic/services     api/sub-services
   :topic/available-jobs api/sub-available-jobs})

(defn capra-subscribe!
  "Subscribe to a PState via Capra using ctx/await."
  [get-token-fn sub-type id on-update-fn]
  (let [sub-fn (get sub-fns sub-type)]
    (if-not sub-fn
      (js/Promise.reject
       (ex-info (str "Unknown subscription type: " sub-type) {:sub-type sub-type}))
      (-> (authenticate! get-token-fn)
          (.then (fn [identity]
                   (let [on-change (capra/callback on-update-fn)
                         on-error  (capra/callback
                                    (fn [err]
                                      (log/warn :capra-dispatch/sub-error
                                                {:sub-type sub-type :id id :error err})))
                         release!  (fn []
                                     (callback/release! on-change)
                                     (callback/release! on-error))
                         ctx       (capra/ctx @capra-client/client)
                         ref       (sub-fn ctx {:id id
                                                :on-change on-change
                                                :on-error on-error
                                                :auth identity})]
                     (-> (capra/await ctx ref)
                         (.then (fn [result]
                                  (let [{:keys [stub data]}
                                        (if (and (map? result)
                                                 (contains? result :capra/sub))
                                          {:stub (:capra/sub result)
                                           :data (:capra/data result)}
                                          {:stub nil :data result})]
                                    (when (instance? capra.stub/CapraStub stub)
                                      (capra.lifecycle/own-callback! stub on-change)
                                      (capra.lifecycle/own-callback! stub on-error))
                                    {:sub-id (when stub (:cap-id stub))
                                     :data   data
                                     :stub   stub})))
                         (.catch (fn [err]
                                   (release!)
                                   (throw err)))))))
          (.catch (fn [err]
                    (when (= :error/auth-failed (:code (ex-data err)))
                      (reset-session!))
                    (log/error :capra-dispatch/subscribe-failed
                               {:sub-type sub-type :id id
                                :error (ex-message err)})
                    (throw err)))))))

(defn capra-unsubscribe!
  "Dispose a Capra subscription stub."
  [stub]
  (when (instance? capra.stub/CapraStub stub)
    (capra/dispose! @capra-client/client stub)
    (log/debug :capra-dispatch/unsubscribed {:cap-id (:cap-id stub)})))
